import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * 页脚链接组类型定义
 */
interface FooterLinkGroup {
  title: string;
  links: Array<{
    text: string;
    href: string;
  }>;
}

/**
 * 导航链接类型定义
 */
interface NavLink {
  text: string;
  href: string;
}

/**
 * 统一页脚组件
 * 包含链接组、社交媒体图标、版权信息和邮件订阅功能
 * @returns {JSX.Element} 渲染的React组件
 */
const Footer: React.FC = () => {
  // 页脚链接组数据
  const linkGroups: FooterLinkGroup[] = [
    {
      title: '公司',
      links: [
        { text: '关于我们', href: '/about' },
        { text: '团队', href: '/team' },
        { text: '联系我们', href: '/contact' }
      ]
    },
    {
      title: '资源',
      links: [
        { text: '博客', href: '/blog' },
        { text: '文档', href: '/docs' },
        { text: '帮助中心', href: '/help' }
      ]
    },
    {
      title: '法律',
      links: [
        { text: '隐私政策', href: '/privacy' },
        { text: '条款', href: '/terms' }
      ]
    }
  ];

  // 社交媒体链接
  const socialLinks: NavLink[] = [
    { text: 'Twitter', href: 'https://twitter.com' },
    { text: 'LinkedIn', href: 'https://linkedin.com' },
    { text: 'GitHub', href: 'https://github.com' }
  ];
  // 邮箱输入状态
  const [email, setEmail] = useState('');
  // 订阅成功状态
  const [subscribed, setSubscribed] = useState(false);

  /**
   * 处理邮件输入变化
   * @param {React.ChangeEvent<HTMLInputElement>} e - 输入事件
   */
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
  };

  /**
   * 处理邮件订阅提交
   * @param {React.FormEvent} e - 表单事件
   */
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // 这里添加邮件订阅逻辑
    console.log('页脚邮件订阅提交:', email);
    // 模拟订阅成功
    setSubscribed(true);
    setEmail('');
    // 5秒后重置状态
    setTimeout(() => setSubscribed(false), 5000);
  };

  return (
    <footer className="bg-secondary text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* 页脚主要内容 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* 公司信息 */}
          <div>
            <Link to="/" className="text-2xl font-bold text-white mb-4 inline-block">佳达掌柜</Link>
            <p className="text-gray-300 mb-6">一个便捷的店铺经营与增长工具</p>
            
            {/* 社交媒体图标 */}
            <div className="flex space-x-4">
              {socialLinks.map((link, index) => (
                <a 
                  key={index} 
                  href={link.href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center hover:bg-primary transition-colors duration-200"
                  aria-label={link.text}
                >
                  {/* 简化的社交媒体图标 */}
                  {link.text === 'Twitter' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  )}
                  {link.text === 'LinkedIn' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" clipRule="evenodd" />
                    </svg>
                  )}
                  {link.text === 'GitHub' && (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                    </svg>
                  )}
                </a>
              ))}
            </div>
          </div>
          
          {/* 链接组 */}
          {linkGroups.map((group, index) => (
            <div key={index}>
              <h3 className="text-lg font-bold mb-4">{group.title}</h3>
              <ul className="space-y-2">
                {group.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link 
                      to={link.href} 
                      className="text-gray-300 hover:text-primary transition-colors duration-200"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          
          {/* 邮件订阅 - 修复后的版本 */}
          <div>
            <h3 className="text-lg font-bold mb-4">订阅我们的通讯</h3>
            <p className="text-gray-300 mb-4">获取最新的产品更新和行业新闻</p>
            
            {subscribed ? (
              <div className="bg-primary/20 text-white p-3 rounded-xl text-sm">
                感谢订阅！我们将很快发送最新资讯给您。
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="w-full">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="输入您的邮箱"
                    className="w-full px-4 py-3 pr-24 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-200"
                    required
                  />
                  <button
                    type="submit"
                    className="absolute right-1 top-1 bottom-1 bg-primary text-white px-4 rounded-lg font-medium hover:bg-opacity-90 transition-all duration-200"
                  >
                    订阅
                  </button>
                </div>
              </form>
            )}
            
            <p className="text-gray-400 text-xs mt-2">
              我们尊重您的隐私，不会向第三方分享您的信息。
            </p>
          </div>
        </div>
        
        {/* 分隔线 */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              &copy; 2020 - {new Date().getFullYear()} 佳达掌柜 | 主办单位：上海天工明理信息技术有限公司 | 沪ICP备2024104469号-1
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 text-sm hover:text-primary transition-colors duration-200">
                隐私政策
              </Link>
              <Link to="/terms" className="text-gray-400 text-sm hover:text-primary transition-colors duration-200">
                服务条款
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;